import { useIsMobile } from "../../hooks/useDevice"
import { usePlayerStore } from "../../stores/usePlayerStore"

// 2026-05-06 — Botones gestuales para mobile.
//
// En desktop los gestos del avatar (correr / saltar / saludar) se
// disparan con teclado: Space mantenido = correr al destino, S =
// saltar, H = saludar. En mobile no hay teclado — necesitamos botones
// dedicados para esas mismas tres acciones.
//
// Layout: tres botones circulares en bottom-center, alineados como
// fila horizontal. La franja queda entre el joystick (bottom-left) y
// el botón de fuego (bottom-right). Los botones envían custom events
// que LocalAvatar escucha y traduce a los mismos refs internos
// (isSpaceDownRef / isJumpingRef / isWavingRef) que usa el desktop.
//
// Solo se monta cuando phase === "incarnated" (ya hay cuerpo). Si el
// Tripulante está en void / descending / ascending, los botones se
// ocultan automáticamente.

const noSelect = {
    userSelect: "none" as const,
    WebkitUserSelect: "none" as const,
    WebkitTouchCallout: "none" as const,
    WebkitTapHighlightColor: "transparent",
    touchAction: "none" as const,
}

const RUN_COLOR = "#7DEFFF"
const JUMP_COLOR = "#FFD27A"
const WAVE_COLOR = "#A8FF8F"

const RUN_RGB = "125,239,255"
const JUMP_RGB = "255,210,122"
const WAVE_RGB = "168,255,143"

interface ButtonProps {
    label: string
    color: string
    rgb: string
    onPointerDown: () => void
    onPointerUp?: () => void
    glyph: string
}

function ActionButton({
    label,
    color,
    rgb,
    onPointerDown,
    onPointerUp,
    glyph,
}: ButtonProps) {
    return (
        <button
            type="button"
            aria-label={label}
            onPointerDown={(e) => {
                e.preventDefault()
                onPointerDown()
                try {
                    ;(e.target as Element).setPointerCapture(e.pointerId)
                } catch {}
            }}
            onPointerUp={(e) => {
                e.preventDefault()
                onPointerUp?.()
            }}
            onPointerCancel={() => onPointerUp?.()}
            onPointerLeave={() => onPointerUp?.()}
            style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                border: `1.5px solid rgba(${rgb}, 0.55)`,
                background: `radial-gradient(circle at 35% 35%, rgba(${rgb}, 0.45) 0%, rgba(${rgb}, 0.15) 60%, rgba(${rgb}, 0.04) 100%)`,
                color,
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 22,
                fontWeight: 700,
                textShadow: `0 0 8px rgba(${rgb}, 0.65)`,
                boxShadow: `0 0 14px rgba(${rgb}, 0.35), inset 0 0 10px rgba(${rgb}, 0.12)`,
                cursor: "pointer",
                ...noSelect,
            }}
        >
            {glyph}
        </button>
    )
}

export function MobileActionButtons() {
    const isMobile = useIsMobile()
    const phase = usePlayerStore((s) => s.phase)
    if (!isMobile) return null
    if (phase !== "incarnated") return null

    const fire = (event: string) => {
        try {
            window.dispatchEvent(new Event(event))
        } catch {}
    }

    return (
        <div
            className="absolute pointer-events-none z-10"
            style={{
                left: 0,
                right: 0,
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 36px)",
                display: "flex",
                justifyContent: "center",
                gap: 14,
                ...noSelect,
            }}
        >
            <div
                style={{
                    display: "flex",
                    gap: 14,
                    pointerEvents: "auto",
                    ...noSelect,
                }}
            >
                {/* Correr — long-press (mientras se mantiene presionado
                    el avatar corre al destino del joystick). */}
                <ActionButton
                    label="Correr"
                    color={RUN_COLOR}
                    rgb={RUN_RGB}
                    glyph="⇒"
                    onPointerDown={() => fire("rsv-domo-run-down")}
                    onPointerUp={() => fire("rsv-domo-run-up")}
                />
                {/* Saltar — un toque dispara el salto (one-shot). */}
                <ActionButton
                    label="Saltar"
                    color={JUMP_COLOR}
                    rgb={JUMP_RGB}
                    glyph="↑"
                    onPointerDown={() => fire("rsv-domo-jump")}
                />
                {/* Saludar — un toque dispara el saludo (one-shot). */}
                <ActionButton
                    label="Saludar"
                    color={WAVE_COLOR}
                    rgb={WAVE_RGB}
                    glyph="✦"
                    onPointerDown={() => fire("rsv-domo-wave")}
                />
            </div>
        </div>
    )
}
